import {
  HsaContentStep,
  hsaContentStepSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import { HsaTextareaField, HsaTextField } from "./hsa-inspector-inputs";
import { HsaInspectorShell } from "./hsa-inspector-shell";

type ContentStep = PartialWithRequiredType<HsaContentStep>;

function bodyParagraphs(step: ContentStep): string[] {
  return (step.body ?? []).filter(
    (paragraph): paragraph is string => typeof paragraph === "string",
  );
}

const HsaContentReadonly = memo(function HsaContentReadonly({
  step,
}: {
  step: ContentStep;
}) {
  const { t } = useTranslation();
  const body = bodyParagraphs(step);
  return (
    <>
      <Flex dir="column" gap="sm">
        <Text fontSize="sm" fontWeight="medium">
          {step.heading || t("No heading")}
        </Text>
        {body.map((paragraph, index) => (
          <Text fontSize="sm" key={index}>
            {paragraph}
          </Text>
        ))}
      </Flex>
      {step.recordsConsent && (
        <Flex dir="column" gap="xs">
          <Flex w="full">
            <RedoBadge color="success" size="xs" text={t("Records consent")} />
          </Flex>
          <Text fontSize="xs" textColor="secondary">
            {t(
              "Continuing past this screen records the shopper's consent (clickwrap); there is no decline branch.",
            )}
          </Text>
        </Flex>
      )}
    </>
  );
});

const HsaContentEditor = memo(function HsaContentEditor({
  step,
  setStep,
}: {
  step: ContentStep;
  setStep: (step: ContentStep) => void;
}) {
  const { t } = useTranslation();
  const body = bodyParagraphs(step);
  return (
    <>
      <HsaTextField
        description={t("Stable identifier; consent answers are keyed by it.")}
        label={t("Handle")}
        onCommit={(contentHandle) => setStep({ ...step, contentHandle })}
        placeholder="welcome"
        value={step.contentHandle}
      />
      <HsaTextField
        label={t("Heading")}
        onCommit={(heading) => setStep({ ...step, heading })}
        placeholder={t("Before you start")}
        value={step.heading}
      />
      <Flex dir="column" gap="sm">
        <Text fontSize="xxs" textColor="secondary">
          {t("Body paragraphs")}
        </Text>
        {body.map((paragraph, index) => (
          <Flex align="flex-start" gap="xs" key={index}>
            <Flex grow={1}>
              <HsaTextareaField
                onCommit={(value) =>
                  setStep({
                    ...step,
                    body: body.map((p, i) => (i === index ? value : p)),
                  })
                }
                value={paragraph}
              />
            </Flex>
            <RedoButton
              aria-label={t("Remove paragraph")}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() =>
                setStep({ ...step, body: body.filter((_, i) => i !== index) })
              }
              size="sm"
            />
          </Flex>
        ))}
        <Flex>
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={() => setStep({ ...step, body: [...body, ""] })}
            size="sm"
            text={t("Add paragraph")}
          />
        </Flex>
      </Flex>
      <RedoCheckbox
        description={t(
          "Continuing past this screen records the shopper's consent (clickwrap).",
        )}
        label={t("Records consent")}
        setValue={(value) =>
          setStep({ ...step, recordsConsent: value === true })
        }
        value={step.recordsConsent ?? false}
      />
    </>
  );
});

const HsaContentInspector = memo(function HsaContentInspector(
  props: StepTypeDetailsProps<ContentStep>,
) {
  const { t } = useTranslation();
  const { step, setStep, onClose, readonly } = props;
  return (
    <HsaInspectorShell
      onClose={onClose}
      subtitle={step.contentHandle}
      title={t("Content screen")}
    >
      <Text fontSize="xs" textColor="secondary">
        {t("An informational screen with a Continue button and no question.")}
      </Text>
      {readonly ? (
        <HsaContentReadonly step={step} />
      ) : (
        <HsaContentEditor setStep={setStep} step={step} />
      )}
    </HsaInspectorShell>
  );
});

export const hsaContentStepType: FrontendStepType<ContentStep> = {
  type: "custom",
  // The persisted schema requires a non-empty heading (the wire schema does
  // not), so enforce it here to keep saves from hard-failing server-side.
  valid: (step) =>
    hsaContentStepSchema.safeParse(step).success && !!step.heading,
  customInspector: HsaContentInspector,
};
